import { useCallback, useEffect, useRef } from 'react';
import type { MarketoFormInstance, MarketoFormProps } from '../marketo-form.types';
import { injectArrowIntoButton, restoreSubmitButton } from '../utils/button-enhancement';
import { disableMarketoStylesheets, removeMarketoStyles } from '../utils/form-styling';

/**
 * Custom hook for managing Marketo form initialization and lifecycle.
 *
 * When `onFormSuccess` is provided, the hook registers a Marketo `onSuccess`
 * handler that calls it and returns `false` to prevent Marketo's default
 * full-page redirect. This makes submission AJAX-only.
 */
export const useMarketoForm = ({
  munchkinId = '590-LMH-444',
  formId = 969,
  baseUrl = '//info.onceforall.com',
  loadOnOpen = false,
  onFormSuccess,
}: MarketoFormProps & {
  onFormSuccess?: () => void;
}) => {
  const initializedRef = useRef(false);
  const loadingRef = useRef(false);
  const stylesRemovedRef = useRef(false);
  const loadedFormsRef = useRef<Set<number>>(new Set());
  const formInstanceRef = useRef<MarketoFormInstance | null>(null);
  // Store callback in a ref so we always call the latest version
  const onFormSuccessRef = useRef(onFormSuccess);
  onFormSuccessRef.current = onFormSuccess;

  const handleFormReady = useCallback(
    (form: MarketoFormInstance) => {
      if (stylesRemovedRef.current) return;

      // Remove Marketo default styles
      removeMarketoStyles(form);

      // Inject arrow into the submit button
      injectArrowIntoButton(form);

      // Store the form instance for later reset
      formInstanceRef.current = form;

      // Intercept submission: call our handler and prevent Marketo's redirect
      if (onFormSuccessRef.current) {
        form.onSuccess(() => {
          onFormSuccessRef.current?.();
          // Returning false tells Marketo to NOT redirect to its thank-you page
          return false;
        });
      }

      loadedFormsRef.current.add(formId);
      loadingRef.current = false;
      initializedRef.current = true;

      // Mark that styles have been removed
      stylesRemovedRef.current = true;
    },
    [formId],
  );

  const initializeForm = useCallback(() => {
    if (loadedFormsRef.current.has(formId)) return;
    if (initializedRef.current || loadingRef.current) return;

    if (typeof window !== 'undefined' && window.MktoForms2) {
      try {
        disableMarketoStylesheets();
        loadingRef.current = true;
        window.MktoForms2.loadForm(baseUrl, munchkinId, formId, handleFormReady);
      } catch {
        // No-op: rely on Script onLoad
      }
    }
  }, [baseUrl, munchkinId, formId, handleFormReady]);

  useEffect(() => {
    initializeForm();
    const handleScriptLoaded = () => {
      initializeForm();
    };
    const handleRetry = () => {
      loadingRef.current = false;
      initializedRef.current = false;
      initializeForm();
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('mkto-forms2-loaded', handleScriptLoaded);
      window.addEventListener('mkto-forms2-retry', handleRetry);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('mkto-forms2-loaded', handleScriptLoaded);
        window.removeEventListener('mkto-forms2-retry', handleRetry);
      }
    };
  }, [initializeForm]);

  useEffect(() => {
    if (loadOnOpen) {
      initializeForm();
    }
  }, [initializeForm, loadOnOpen]);

  const resetForm = useCallback(() => {
    const form = formInstanceRef.current;
    if (!form) return;

    const formEl = form.getFormElem()[0];
    if (!formEl) return;

    // Reset all visible form fields to empty
    const inputs = formEl.querySelectorAll<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >('input:not([type="hidden"]), textarea, select');
    inputs.forEach((input) => {
      if (input.type === 'checkbox' || input.type === 'radio') {
        (input as HTMLInputElement).checked = false;
      } else {
        input.value = '';
      }
    });

    // Restore submit button from "Please Wait..." state
    restoreSubmitButton(form);
  }, []);

  return {
    formId,
    resetForm,
  };
};
